import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import Link from '../misc/Link'
import { toggleModal, setBannerClosed } from 'store/ui/actions'
import styles from './styles/Banner.css'
import classNames from 'classnames'

import qs from 'query-string'
import capitalize from 'lodash.capitalize'
import dateString from 'lib/date-string'

const VISUALIZATIONS_EXAMPLE = '/visualizations/global-analysis-of-genome-transcriptome-9L4dJr?dois[0]=10.1038%2Fmsb.2012.40&dois[1]=10.7554%2Felife.05068&focusedElement=10.7554%2Felife.05068'
const DEFAULT_BANNER_CONTENT = null

const licenseMessage = (license) => {
  const institutionName = license?.name?.split(' ').map(capitalize).join(' ') || 'Your institution'
  if (license?.status === 'TRIALING' || license?.status === 'TRIAL_READY') {
    if (license?.trialEndDate) {
      return `${institutionName} is trialing scite until ${dateString(license?.trialEndDate)}.`
    } else {
      return `${institutionName} is trialing scite.`
    }
  }
  return `${institutionName} subscribes to an organization-wide license of scite.`
}

const getContent = ({ dispatch, bannerContent, subscription, email, license, referralPartner, activatedTrial }) => {
  const banners = {
    visualizations: {
      to: VISUALIZATIONS_EXAMPLE,
      content: (
        <>
          <img alt='tag' className={styles.tagImg} src={`${CONFIG.assetsBaseURL}/images/icons/tag.svg`} />
          Announcing Visualizations: see scite Smart Citations in context. <span className={styles.bannerLink}>Try it now.</span>
        </>
      )
    },
    zotero: {
      href: 'https://github.com/scitedotai/scite-zotero-plugin',
      content: (
        <>
          <img alt='tag' className={styles.tagImg} src={`${CONFIG.assetsBaseURL}/images/icons/tag.svg`} />
          Announcing the scite Zotero Plugin: See how articles in your library have been supported, contrasted, or mentioned by other articles. <span className={styles.bannerLink}>Try Now.</span>
        </>
      )
    },
    wiley: {
      handleClick: () => dispatch(toggleModal('login')),
      content: (
        <>
          Wiley provides 1 free scite report. Sign up now and access all scite features. <span className={styles.bannerLink}> Start your trial now.</span>
        </>
      )
    },
    reprints: {
      handleClick: () => dispatch(toggleModal('login')),
      content: (
        <>
          Enjoy your Article Galaxy access to this full scite report page. If you'd like to explore more beyond this report, <span className={styles.bannerLink}> sign up for a {CONFIG.trialLength}-day trial.</span>
        </>
      )
    },
    citationStatementSearch: {
      href: 'https://scite.ai/search/citations',
      content: (
        <>
          <img alt='tag' className={styles.tagImg} src={`${CONFIG.assetsBaseURL}/images/icons/tag.svg`} />
          Find evidence, analyses, and expert opinions on any subject. <span className={styles.bannerLink}>Search over {CONFIG.defaultCitationsTotalText} Citation Statements.</span>
        </>
      )
    },
    upgrade: {
      to: '/pricing',
      content: (
        <div>
          <span className={styles.bannerLink}>Upgrade now</span> to continue using scite for as low as $16.00/month.
        </div>
      )
    },
    startTrial: {
      href: `${CONFIG.sciteFrontendURL}${CONFIG.stripe.trialCreationURL}`,
      content: (
        <div>
          <span className={styles.bannerLink}>Activate {CONFIG.trialLength} day trial</span> to get an unlimited 7 day preview of scite features. Use ACTIVATEBANNER20 to get 20% off after your trial.
        </div>
      )
    },
    temporaryOutage: {
      content: (
        <>
          We are currently experiencing intermittent technical issues and are actively working to resolve them, sorry for any inconvenience this may cause!
        </>
      )
    },
    organizationLicense: {
      content: (
        <>
          Welcome! {licenseMessage(license)} {
            email
              ? (
                <>
                  Check out our <Link href='https://help.scite.ai/' target='_blank' rel='noopener noreferrer' className={styles.bannerLink}>FAQ / Help</Link> or see what we offer <Link to='/partners/researchers' className={styles.bannerLink}>for researchers</Link>.
                </>
                )
              : (
                <>
                  You can <Link onClick={() => dispatch(toggleModal('login'))} className={styles.bannerLink}>sign up or sign in</Link> to access features that require email (i.e. notifications, dashboards, and reference checks).
                </>
                )
          }
        </>
      )
    }
  }

  if (CONFIG.temporaryOutage) {
    return banners.temporaryOutage
  }

  if (bannerContent === 'organizationLicense') {
    return banners.organizationLicense
  }

  if (referralPartner === 'wiley' && !subscription) {
    return banners.wiley
  }

  if (referralPartner === 'reprints' && !subscription) {
    return banners.reprints
  }

  // post-trialing
  if (subscription?.plan === 'free' && activatedTrial) {
    return banners.upgrade
  }

  if (subscription?.plan === 'free' && !activatedTrial) {
    return banners.startTrial
  }

  if (!bannerContent) {
    return banners[DEFAULT_BANNER_CONTENT]
  }

  return banners[bannerContent]
}

const Banner = () => {
  const dispatch = useDispatch()

  const [checkedStorage, setCheckedStorage] = useState(false)
  const bannerContent = useSelector(({ ui }) => ui.bannerContent)
  const bannerClosed = useSelector(({ ui }) => ui.bannerClosed)
  const referralPartner = useSelector(({ ui }) => ui.referralPartner)
  const license = useSelector(({ ui }) => ui.organizationLicense)
  const user = useSelector(({ ui }) => ui.user)
  const content = getContent({
    dispatch,
    bannerContent,
    subscription: user?.subscription,
    email: user?.email,
    referralPartner,
    license,
    activatedTrial: user?.activatedTrial
  })

  const { search, pathname } = useLocation()
  const query = qs.parse(search)

  const handleClose = () => {
    const { localStorage } = window
    if (localStorage) {
      localStorage.setItem('banner-generic-3', 'closed')
    }
    dispatch(setBannerClosed(true))
  }

  useEffect(() => {
    if (CONFIG.temporaryOutage) {
      setCheckedStorage(true)
      dispatch(setBannerClosed(false))
      return
    }

    const { localStorage } = window
    if (localStorage?.getItem('banner-generic-3') === 'closed' || query.bannerClosed === 'true') {
      dispatch(setBannerClosed(true))
    }

    setCheckedStorage(true)

    if (pathname.includes('/search/citations') && (!bannerContent || bannerContent === 'citationStatementSearch')) {
      dispatch(setBannerClosed(true))
    }
  }, [])

  if (!content) {
    return null
  }

  if (!checkedStorage) {
    return null
  }

  return (
    <div
      className={classNames(styles.navBanner, {
        [styles.bannerClosed]: bannerClosed
      })}
    >
      <div className={styles.closeBtn} onClick={() => handleClose()}>
        <i className='fa fa-lg fa-times' />
      </div>
      {(content.href || content.to || content?.handleClick)
        ? (
          <Link
            className={styles.link}
            href={content?.href}
            to={content?.to}
            onClick={content?.handleClick}
          >
            {content.content}
          </Link>
          )
        : (
          <div>{content.content}</div>
          )}
    </div>
  )
}

export default Banner
